/**
 * The app-wide "Oct 2, 2026" date style, in two flavours that must not be mixed up.
 *
 * - Calendar dates (invoice_date, due_date, order_date, received_date, SAP document and
 *   posting dates) are a day on a printed document, not a moment in time. They render the
 *   same day for every viewer, whatever their time zone.
 * - Instants (created_at, updated_at, received_at) are moments in time and render in the
 *   viewer's local zone.
 *
 * Never format a calendar date with `new Date('YYYY-MM-DD')`: JavaScript parses it as
 * midnight UTC, so viewers west of UTC see the previous day.
 */

const DATE_STYLE: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' }
const CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})$/

/** A calendar date ("2026-10-02") as "Oct 2, 2026" in every time zone. A value with a time falls back to formatInstantDate. */
export function formatCalendarDate(value: string | null | undefined): string {
  if (!value) return '—'
  const parts = CALENDAR_DATE.exec(value)
  if (!parts) return formatInstantDate(value)
  const [, year, month, day] = parts
  const d = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)))
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleDateString('en-US', { ...DATE_STYLE, timeZone: 'UTC' })
}

/** The local-zone day of a timestamp ("2026-10-09T15:04:00Z") as "Oct 9, 2026". A bare calendar date is routed to formatCalendarDate. */
export function formatInstantDate(value: string | null | undefined): string {
  if (!value) return '—'
  if (CALENDAR_DATE.test(value)) return formatCalendarDate(value)
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleDateString('en-US', DATE_STYLE)
}
